import { useId, useMemo, useState } from "react";
import { Drawer, Typography } from "@mui/material";
import { CoreSearchField, CoreTable } from "@verity/components/core";
import type { CoreTableColumn } from "@verity/components/core";
import i18n from "../../../../../i18n";
import noRunningApplicationsImage from "./assets/no-running-applications.png";
import { IncorrectIcon } from "./CollaborationDetection/assets";
import styles from "./RunningApplicationsModal.module.css";

export function RunningApplicationsModal({
	open,
	onClose,
	applications,
}: {
	open: boolean;
	onClose: () => void;
	applications: string[];
}) {
	const [search, setSearch] = useState("");
	const tableId = useId();
	const titleId = useId();

	const filteredApplications = useMemo(
		() =>
			applications
				.filter((application) => (search ? application.toLowerCase().includes(search.toLowerCase()) : true))
				.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())),
		[applications, search],
	);

	const tableData = useMemo(
		() => filteredApplications.map((application) => ({ name: application })),
		[filteredApplications],
	);

	const columns: CoreTableColumn<{ name: string }>[] = [
		{
			header: (
				<Typography>
					{i18n.t("result.components.resultsPage.runningApplicationsModal.tableHeader", {
						count: filteredApplications.length,
					})}
				</Typography>
			),
			key: "name",
			descendingComparator: (a, b) => (a.name ?? "").toLowerCase().localeCompare((b.name ?? "").toLowerCase()),
			cell: (item) => item.data.name,
		},
	];

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			sx={{ zIndex: 1300 }}
			PaperProps={{ className: styles.paper, role: "dialog", "aria-modal": "true", "aria-labelledby": titleId }}
		>
			<div className={styles.header}>
				<Typography id={titleId} variant="h2" className={styles.title}>
					{i18n.t("result.components.resultsPage.runningApplicationsModal.title")}
				</Typography>
				<button
					type="button"
					aria-label={i18n.t("result.components.resultsPage.runningApplicationsModal.close") as string}
					onClick={onClose}
					className={styles.closeButton}
				>
					<IncorrectIcon width="24px" height="24px" aria-hidden="true" />
				</button>
			</div>
			<div className={styles.container}>
				<Typography className={styles.description}>
					{i18n.t("result.components.resultsPage.runningApplicationsModal.description")}
				</Typography>
				{applications.length === 0 ? (
					<div className={styles.emptyState}>
						<img src={noRunningApplicationsImage} alt="" />
						<div className={styles.emptyStateText}>
							<Typography variant="h3" className={styles.emptyStateTitle}>
								{i18n.t("result.components.resultsPage.runningApplicationsModal.emptyTitle")}
							</Typography>
							<Typography className={styles.emptyStateDescription}>
								{i18n.t("result.components.resultsPage.runningApplicationsModal.emptyDescription")}
							</Typography>
						</div>
					</div>
				) : (
					<div className={styles.tableContainer}>
						<CoreSearchField
							ariaControls={tableId}
							label={i18n.t("result.components.resultsPage.runningApplicationsModal.search") as string}
							totalResults={filteredApplications.length ?? 0}
							onSearch={(value) => setSearch(value)}
							value={search}
							fullWidth
							searchFieldWrapperClassname={styles.searchField}
						/>
						<div className={styles.tableScroll}>
							<CoreTable
								id={tableId}
								aria-label={i18n.t("result.components.resultsPage.runningApplicationsModal.title") as string}
								columns={columns}
								data={tableData}
								maxHeight="100%"
								stickyHeader
								noResultsString={i18n.t("result.components.resultsPage.runningApplicationsModal.noResults") as string}
								allowUIOverrides
								rowHeight={50}
								rowsPerPage={Math.max(tableData.length, 1)}
								rowProps={{ tabIndex: 0, className: styles.row }}
							/>
						</div>
					</div>
				)}
			</div>
		</Drawer>
	);
}
